import React, { useRef, useCallback, useEffect } from "react";
import ScrollArea from "react-scrollbars-custom";
import { compose } from "redux";
import { connect } from "react-redux";
import { debounce } from 'throttle-debounce';

import actions from "fantastic/actions";
import connectActions from "fantastic/lib/connectActions";
import connectProps from "fantastic/lib/connectProps";
import isLightTheme from "fantastic/lib/isLightTheme";

import BackgroundSettings from "fantastic/components/common/controls/background/Settings";
import ExtendedThemeSettings from './ExtendedThemeSettings';
import FontSettings from "fantastic/components/common/controls/font/Settings";
import SinglePaddingSettings from "fantastic/components/common/controls/wrapper/Padding";
import ToggleSetting from "fantastic/components/common/controls/ToggleSetting";

const ThemeSettings = ({ updateTheme, saveTheme, theme: { style_attributes }, creatorMode }) => {
  const debouncedSaveTheme = useCallback(debounce(2000, saveTheme), []);
  const scrollRef = useRef(null);

  const onChange = (field, value) => {
    updateTheme({ [field]: value });
    debouncedSaveTheme();
  };
  const scrollToIfNeeded = (element) => {
    scrollRef.current.scrollTo(0, element.offsetTop);
  };
  const backgroundParams = {
    onChange,
    scrollToIfNeeded,
    defaultColor: '#FFFFFF',
    color: { field: "base-bg", value: style_attributes["base-bg"] },
    image: { field: "base-bg-image", value: style_attributes["base-bg-image"] },
    repeat: { field: "base-bg-repeat", value: style_attributes["base-bg-repeat"] },
    position: { field: "base-bg-position", value: style_attributes["base-bg-position"] }
  };
  const fontParams = {
    onChange,
    scrollToIfNeeded,
    label: i18n.t('text'),
    color: { field: "base-font-color", value: style_attributes["base-font-color"] },
    family: { field: "base-font-family", value: style_attributes["base-font-family"] },
    size: { field: "body-font-size", value: style_attributes["body-font-size"] }
  };
  const linkFontParams = {
    onChange,
    scrollToIfNeeded,
    onlyColor: true,
    label: i18n.t('link_color'),
    color: { field: "anchor-font-color", value: style_attributes["anchor-font-color"] },
  };
  const hFontParams = (n) => {
    return {
      scrollToIfNeeded,
      onChange,
      label: `${i18n.t('heading')} ${n}`,
      direction: n > 1 ? 'up' : 'down',
      color: { field: `h${n}-font-color`, value: style_attributes[`h${n}-font-color`] },
      family: { field: `h${n}-font-family`, value: style_attributes[`h${n}-font-family`] },
      size: { field: `h${n}-font-size`, value: style_attributes[`h${n}-font-size`] }
    };
  };
  const paddingTopParams = {
    onChange,
    label: i18n.t('column_top_padding'),
    padding: { field: 'wrapper-padding-top', value: style_attributes["wrapper-padding-top"] }
  };
  const paddingBottomParams = {
    onChange,
    label: i18n.t('column_bottom_padding'),
    padding: { field: 'column-cell-padding-bottom', value: style_attributes["column-cell-padding-bottom"] }
  };
  const adaptiveDesignParams = {
    onChange,
    label: i18n.t('adaptive'),
    field: 'include-html-media-query-classes',
    value: style_attributes['include-html-media-query-classes']
  };

  const contrastMode = 'dark';

  const colorSchemeParams = {
    onChange,
    label: i18n.t('contrast_settings'),
    field: 'include-html-color-scheme-classes',
    value: style_attributes['include-html-color-scheme-classes']
  };

  const gutterParams = {
    onChange,
    label: i18n.t('column_gutter'),
    padding: { field: 'column-gutter', value: style_attributes["column-gutter"] },
    max: 50,
    min: 0
  };

  const backgroundContrastParams = {
    label: i18n.t('background_contrast'),
    onChange,
    scrollToIfNeeded,
    defaultColor: '#222222',
    color: { field: "base-bg-contrast", value: style_attributes["base-bg-contrast"] },
    image: { field: "base-bg-image-contrast", value: style_attributes["base-bg-image-contrast"] },
    repeat: { field: "base-bg-repeat-contrast", value: style_attributes["base-bg-repeat-contrast"] },
    position: { field: "base-bg-position-contrast", value: style_attributes["base-bg-position-contrast"] }
  };

  const fontContrastParams = {
    onChange,
    scrollToIfNeeded,
    label: i18n.t('text_contrast'),
    color: { field: "base-font-color-contrast", value: style_attributes["base-font-color-contrast"] }
  };

  const paragraphMarginBottomParams = {
    onChange,
    label: i18n.t('paragraph_margin_bottom'),
    padding: { field: 'paragraph-margin-bottom', value: style_attributes["paragraph-margin-bottom"] }
  };

   useEffect(() => {
     const mode = isLightTheme(style_attributes['base-bg'], style_attributes['base-font-color']) ? 'light' : 'dark';
     if((style_attributes['contrast-color-scheme'] === 'light' ? 'dark' : 'light') !== mode) {
       onChange('contrast-color-scheme', mode === 'light' ? 'dark' : 'light')
     }
   }, [style_attributes['base-bg'], style_attributes['base-font-color']])


  return (
    <div className="fantastic-sidebar__body">
      <ScrollArea noDefaultStyles={true} ref={scrollRef}>
        <div className="fantastic-sidebar__scroll-area">
          <BackgroundSettings {...backgroundParams} />
          <ToggleSetting {...adaptiveDesignParams} />
          <FontSettings {...fontParams} />
          <FontSettings {...linkFontParams} />
          { [1,2,3,4,5,6].map((n) => {
            return <FontSettings {...hFontParams(n)} key={`header-${n}`} />
          })}
          <SinglePaddingSettings {...paddingTopParams} />
          <SinglePaddingSettings {...paddingBottomParams} />
          <SinglePaddingSettings {...gutterParams} />
          <SinglePaddingSettings {...paragraphMarginBottomParams} />
          { (creatorMode || style_attributes['include-html-color-scheme-classes']) && <ToggleSetting {...colorSchemeParams} /> }
          { style_attributes['include-html-color-scheme-classes'] &&
            <>
            <BackgroundSettings {...backgroundContrastParams} />
            <FontSettings {...fontContrastParams} />
            </>
          }


          { creatorMode && <><hr /><ExtendedThemeSettings onChange={onChange} styleAttributes={style_attributes} /></> }
        </div>
      </ScrollArea>
    </div>
  );
};

export default compose(
  connectProps("theme"),
  connectActions(actions)
)(ThemeSettings);
